// 056 — Atención Adolescentes (Anexo 1 MSP, SNS-MSP/HCU-form. 056/2021). Bloques A-R del papel (páginas 192-193 del PDF); datos y
// decisiones en `bloques056Datos.ts`. Al guardar deja, en UNA transacción, el `Encounter.reasonCode` (motivo), una `Condition`
// por diagnóstico, el `Observation` de hallazgos del examen físico y los `Observation` de signos vitales numéricos; el resto
// va como `Par[]` en el PDF firmado.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { Bloque } from './camposSimples';
import { MARCAS_PRE_DEF } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import { SeccionCampos } from './SeccionCampos';
import { observacionesDeToma } from './signosVitalesToma';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { BLOQUES_056, codigosDiagnostico056, estadoInicial056, hallazgosDe056, motivoDe056, paresDe056, tomaDe056, type Estado056 } from './bloques056Datos';

const CODIGO = '056';

export function Formulario056({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f056.titulo');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);

  const [estado, setEstado] = useState<Estado056>(() => estadoInicial056());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const ahoraIso = new Date().toISOString();
      const lote = new LoteTransaccional();
      const motivo = motivoDe056(estado);
      if (motivo && encounter) {
        const actual = await medplum.readReference(encounter);
        lote.actualizarEncounterAbierto({ ...actual, id: actual.id!, reasonCode: [{ text: motivo }] });
      }
      for (const code of codigosDiagnostico056(estado)) lote.crear({ resourceType: 'Condition', subject, encounter, code, recordedDate: ahoraIso });
      const hallazgos = hallazgosDe056(estado);
      if (hallazgos) {
        lote.crear({ resourceType: 'Observation', status: 'final', subject, encounter, code: { text: 'Hallazgos de examen físico' }, valueString: hallazgos, effectiveDateTime: ahoraIso });
      }
      for (const o of observacionesDeToma(tomaDe056(estado), subject, encounter, ahoraIso)) lote.crear(o);
      await lote.enviar(medplum);
      onGuardado(paresDe056(estado), datosEncabezadoMsp);
    });

  const diagnosticos = (
    <Bloque titulo={t('formularios.f056.diagnosticoTitulo')}>
      <p className="text-xs text-hce-muted">{t('formularios.f056.diagnosticoNota')}</p>
      {estado.diagnosticos.map((d, i) => (
        <SelectorDiagnosticoConMarca
          key={i}
          label={`${t('formularios.f056.diagnosticoRotulo')} ${i + 1}`}
          marcaLabel={t('formularios.f056.marcaDiagnostico')}
          marcas={MARCAS_PRE_DEF}
          value={d}
          onChange={(v) => setEstado((prev) => ({ ...prev, diagnosticos: prev.diagnosticos.map((x, j) => (j === i ? v : x)) }))}
        />
      ))}
    </Bloque>
  );

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      {BLOQUES_056.map((b) => (
        <div key={b.clave} className="flex flex-col gap-6">
          <Bloque titulo={b.titulo}>
            {b.clave === 'percepcion' && <p className="text-xs text-hce-muted">{t('formularios.f056.familiogramaNota')}</p>}
            <SeccionCampos
              prefijoId={`f056-${b.prefijo}`}
              campos={b.campos}
              valores={estado.bloques[b.clave] ?? {}}
              columnas={b.columnas}
              onChange={(v) => setEstado((prev) => ({ ...prev, bloques: { ...prev.bloques, [b.clave]: v } }))}
            />
          </Bloque>
          {b.clave === 'examen' && diagnosticos}
        </div>
      ))}

      <Bloque titulo={t('formularios.f056.responsableTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
